/** Inline degradation indicator.
 *
 *  Renders a tiny dot in the top-right of the host pane only when
 *  REAL user-facing requests are failing. Escalates to a banner row
 *  only on sustained failure (red). Renders nothing during silence
 *  or pure synthetic-probe yellows.
 *
 *  Philosophy (2026-05-11 dispatch):
 *    Don't manufacture outages. Synthetic probes are informational
 *    only. The truth source is the `user_requests_recent` probe,
 *    which reads recent /api/bridge/* outcomes from the daemon. If
 *    user calls are succeeding the indicator is silent regardless
 *    of what the bridge canary thinks.
 *
 *  Usage:
 *    <DegradationBanner />  // defaults to user_requests_recent
 *
 *  The component does not own retry; it lets /api/status/full do the
 *  probing and just renders the result. Polling cadence is 60 s
 *  (matches the bridge probe's own cache TTL); the visibility-change
 *  handler refetches immediately on tab focus so the indicator never
 *  sits stale after a long tab-switch. */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, XCircle, X, RotateCcw } from "lucide-react";
import { csrfFetch } from "../lib/auth";

type StatusState = "green" | "yellow" | "red" | "unknown";

type StatusItem = {
  id: string;
  label: string;
  state: StatusState;
  hint: string;
};

type StatusResponse = {
  items: StatusItem[];
};

// 60 s matches the bridge probe's own server-side cache TTL: there's
// no point polling faster than the daemon will give us new data, and
// the indicator updates instantly on tab focus regardless.
const POLL_INTERVAL_MS = 60_000;

type RestartState = "idle" | "running" | "ok" | "error";

// Default to the real-user-pain probe. The legacy synthetic-probe
// list (outlook_bridge etc.) was generating false outage banners on
// every transient COM-serialization stretch. Callers can still pass
// an explicit `deps` array for legacy surfaces, but the mail view
// (and any new caller) should leave this defaulted so the indicator
// reflects actual experience, not a canary's guess at one.
const DEFAULT_DEPS = ["user_requests_recent"];

export function DegradationBanner({ deps = DEFAULT_DEPS }: { deps?: string[] } = {}) {
  // The effect below keys on the ids as a string. It used to key on the
  // `deps` array itself, and the default was a new array on every render, so
  // each status answer (which sets state and re-renders) re-ran the effect,
  // which fetched again: an endless /api/status/full loop as soon as that
  // endpoint answered 200.
  const depsKey = deps.join(",");
  const [items, setItems] = useState<StatusItem[] | null>(null);
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(() => new Set());
  const [restartState, setRestartState] = useState<RestartState>("idle");
  const [restartMsg, setRestartMsg] = useState<string>("");

  const restartOutlookVm = async () => {
    setRestartState("running");
    setRestartMsg("");
    try {
      // The endpoint can take up to ~45 s while restart-outlook.ps1 polls
      // for the listener to come back; allow two minutes.
      const r = await csrfFetch("/api/admin/restart-outlook-vm", { method: "POST" }, { timeoutMs: 120_000 });
      const body = (await r.json().catch(() => ({ ok: false, error: `HTTP ${r.status}` }))) as { ok?: boolean; error?: string; exitCode?: number };
      if (body.ok) {
        setRestartState("ok");
        setRestartMsg("Outlook restarted. Bridge should be back within a few seconds.");
      } else {
        setRestartState("error");
        setRestartMsg(body.error ?? `exit code ${body.exitCode}`);
      }
    } catch (e) {
      setRestartState("error");
      setRestartMsg((e as Error).message);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let ctl = new AbortController();
    const load = async () => {
      // Each fetch uses its own AbortController so a refetch
      // triggered by visibility-change cancels any in-flight prior
      // call instead of racing it.
      ctl.abort();
      ctl = new AbortController();
      try {
        const r = await fetch("/api/status/full", {
          credentials: "same-origin",
          cache: "no-store",
          signal: ctl.signal,
        });
        if (!r.ok) return;
        const body = (await r.json()) as StatusResponse;
        if (cancelled) return;
        const wanted = depsKey.split(",");
        setItems(body.items.filter((i) => wanted.includes(i.id)));
      } catch {
        // network/abort: silent — leave previous state in place so
        // the banner doesn't flicker on every cell-network hiccup.
      }
    };
    load();
    const intervalId = setInterval(load, POLL_INTERVAL_MS);
    // Refetch immediately when the tab regains focus or becomes
    // visible. Without this, the banner can sit on stale state for
    // up to POLL_INTERVAL_MS after a long tab-switch, which is
    // exactly the "banner says bridge wedged but /status is green"
    // case Waqas reported.
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    const onFocus = () => load();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      ctl.abort();
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onFocus);
    };
  }, [depsKey]);

  if (!items) return null;
  const degraded = items.filter(
    (i) => (i.state === "yellow" || i.state === "red") && !dismissedIds.has(`${i.id}:${i.state}`),
  );
  if (degraded.length === 0) return null;

  const worst: StatusState = degraded.some((i) => i.state === "red") ? "red" : "yellow";

  // Yellow path: subtle inline indicator only. A dot in the corner
  // with a tooltip; no row, no dismissable banner. This is the "a
  // single user call failed in the last 30 s" case and the user
  // doesn't need a wall of text about it.
  if (worst === "yellow") {
    const hint = degraded[0]?.hint ?? "Recent request failed.";
    return (
      <div className="pointer-events-auto absolute right-3 top-3 z-20 inline-flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50/90 px-1.5 py-0.5 text-[10px] text-amber-800 backdrop-blur dark:border-amber-800 dark:bg-amber-950/80 dark:text-amber-200" role="status" title={hint}>
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
        <span className="hidden sm:inline">recent request slow</span>
        <Link to="/status" className="underline opacity-70 hover:opacity-100">details</Link>
      </div>
    );
  }

  // Red path: full banner with the recovery affordance, because the
  // user is actually unable to use the app. Self-heal is meaningful
  // when the Outlook bridge dep is red.
  const Icon = XCircle;
  const tone =
    "border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-800 dark:bg-rose-950 dark:text-rose-100";
  const outlookDegraded = degraded.some(
    (i) => i.id === "outlook_bridge" || i.id === "user_requests_recent",
  );
  const copyFor = (i: StatusItem) => {
    if (i.id === "user_requests_recent") {
      return `Mail surface failing: ${i.hint}`;
    }
    return i.hint || `${i.label} unreachable.`;
  };

  return (
    <div className={`mb-3 flex items-start gap-3 rounded-md border px-3 py-2 text-sm ${tone}`} role="status">
      <Icon className="mt-0.5 h-4 w-4 flex-none" aria-hidden />
      <div className="min-w-0 flex-1">
        {degraded.map((i) => (
          <div key={i.id} className="leading-snug">
            {copyFor(i)}
          </div>
        ))}
        <div className="mt-1 flex items-center gap-3 text-xs opacity-80">
          <Link to="/status" className="underline">
            View status
          </Link>
          {outlookDegraded && (
            <button
              type="button"
              onClick={restartOutlookVm}
              disabled={restartState === "running"}
              className="inline-flex items-center gap-1 rounded border border-current px-1.5 py-0.5 text-xs opacity-80 hover:opacity-100 disabled:opacity-50"
            >
              <RotateCcw className="h-3 w-3" aria-hidden />
              {restartState === "running" ? "Restarting Outlook..." : "Restart Outlook"}
            </button>
          )}
          {restartState === "ok" && (
            <span className="text-xs opacity-80">{restartMsg}</span>
          )}
          {restartState === "error" && (
            <span className="text-xs opacity-80">Restart failed: {restartMsg}</span>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => {
          setDismissedIds((prev) => {
            const next = new Set(prev);
            for (const i of degraded) next.add(`${i.id}:${i.state}`);
            return next;
          });
        }}
        className="ml-2 flex-none rounded p-1 text-current opacity-60 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
        aria-label="Dismiss banner"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
